import { useState } from 'react';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { Check, PenLine } from 'lucide-react';
import { ProfileActions } from 'store/actions';
import SignaturePad from 'components/SignaturePad';
import { SavedSignature } from 'components/Profile';
import { choiceBtnUnselected } from 'components/Profile/helper';
import DocumentModal from 'components/Profile/Consent/DocumentModal';
import { consentPayload, signedDate } from 'components/Profile/Consent/helper';

const enhancer = connect(null, { ...ProfileActions });

const TreatmentConsentModal = ({ consent, createConsent, onClose, onSigned }) => {
  const { title, content, pdf_url, status, signature_url } = consent?.attributes || {};
  const [signing, setSigning] = useState(false);
  const [signature, setSignature] = useState(null);

  const handleSign = async () => {
    setSigning(true);

    try {
      const { data } = await createConsent(consentPayload([consent.id], 'treatment', signature));

      toast.success('Consent signed successfully');
      onSigned(data);
      onClose();
    } catch (error: any) {
      toast.error(error.errors || 'Failed to sign this consent. Please try again later.');
      setSigning(false);
    }
  };

  return (
    <DocumentModal
      label="Treatment consent"
      title={title}
      onClose={onClose}
      footer={
        <>
          {status ? (
            <p className="flex items-center gap-2 text-[13px] font-semibold text-success-600">
              <Check size={15} strokeWidth={3} /> Signed copy — kept on file
            </p>
          ) : (
            <p className="hidden text-[13px] text-secondary-400 sm:block">
              By signing, I confirm I have read this consent, had the chance to ask questions, and agree to the treatment described.
            </p>
          )}

          <div className="flex shrink-0 items-center gap-3">
            <button type="button" onClick={onClose} disabled={signing} className={`btn h-10 min-h-0 rounded-xl font-semibold ${choiceBtnUnselected}`}>
              Close
            </button>

            {!status && (
              <button
                type="button"
                onClick={handleSign}
                disabled={signing || !signature}
                className="btn h-10 min-h-0 gap-2 whitespace-nowrap rounded-xl border-none bg-secondary-900 font-semibold text-white hover:bg-black disabled:bg-secondary-900 disabled:text-white disabled:opacity-60"
              >
                {signing ? <span className="loading loading-spinner loading-sm" /> : <><PenLine size={15} strokeWidth={2.5} /> Sign &amp; continue</>}
              </button>
            )}
          </div>
        </>
      }
    >
      {pdf_url ? (
        <div className="min-h-0 overflow-hidden">
          <iframe
            src={`${pdf_url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            title={title}
            className="-m-2.5 aspect-[1/1.15] w-[calc(100%+1.25rem)] border-0 sm:-m-5 sm:w-[calc(100%+2.5rem)]"
          />
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
          <div dangerouslySetInnerHTML={{ __html: content || '' }} />

          {/* Signed but the PDF is still generating: close the content with the signature, as the PDF will. */}
          {signature_url && <SavedSignature url={signature_url} signedOn={signedDate(consent?.attributes)} />}
        </div>
      )}

      {!status && (
        <div className="shrink-0 border-t border-gray-100 px-4 py-3 sm:px-6">
          <SignaturePad signature={signature} onSignature={setSignature} />
        </div>
      )}
    </DocumentModal>
  );
};

export default enhancer(TreatmentConsentModal);
